import { listAccounts, listCategories, type CategoryListItem } from '../api-client';
import { navigate } from '../router';
import { openIncomeSheet } from '../components/income-sheet';
import { renderLogInput } from './log-input';
import { renderFeed } from './feed';
import { renderEmptyState } from './empty-state';

export function renderHomePage(root: HTMLElement, user: { displayName: string }) {
  root.innerHTML = `
    <div class="home">
      <header class="home-header">
        <span class="home-hello">Hello, ${escapeHtml(user.displayName)}</span>
        <div class="home-header-actions">
          <a href="/" class="nav-ico" id="home-link" aria-label="Control Centre">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M3 9.5 L10 3 L17 9.5 V17 H13 V13 H7 V17 H3 Z"></path>
            </svg>
          </a>
          <a href="/dashboard" class="nav-ico" id="dash-link" aria-label="Dashboard">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="2.5" y="5" width="15" height="11" rx="2.5"></rect>
              <path d="M2.5 8.5 h15"></path>
              <circle cx="14" cy="12" r="1"></circle>
            </svg>
          </a>
          <a href="/reports" class="nav-ico" id="reports-link" aria-label="Reports">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="4" y1="17" x2="4" y2="9"></line>
              <line x1="10" y1="17" x2="10" y2="3"></line>
              <line x1="16" y1="17" x2="16" y2="12"></line>
            </svg>
          </a>
          <a href="/sms-inbox" class="nav-ico" id="sms-link" aria-label="SMS Inbox">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="2.5" y="4" width="15" height="10" rx="2"></rect>
              <path d="M5.5 8h9M5.5 11h5"></path>
            </svg>
          </a>
          <a href="/settings" class="nav-ico" id="gear-link" aria-label="Settings">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="3" y1="7" x2="17" y2="7"></line>
              <circle cx="8" cy="7" r="2.1" fill="var(--bg)"></circle>
              <line x1="3" y1="13" x2="17" y2="13"></line>
              <circle cx="13" cy="13" r="2.1" fill="var(--bg)"></circle>
            </svg>
          </a>
        </div>
      </header>
      <section class="home-input"></section>
      <button class="add-income-link" id="add-income" type="button">+ Add income</button>
      <div id="income-sheet-mount"></div>
      <section class="home-feed"></section>
    </div>
  `;

  const inputHost = root.querySelector<HTMLElement>('.home-input')!;
  const feedHost = root.querySelector<HTMLElement>('.home-feed')!;

  // Categories are needed for ID → name resolution in the feed. Fetch once on
  // mount, refresh on every feed re-render (cheap; categories rarely change).
  let categories: CategoryListItem[] = [];

  renderLogInput(inputHost, { onTransactionCreated: () => refreshFeed() });

  // Initial load: fetch categories first, then the feed.
  void (async () => {
    try {
      categories = (await listCategories()).categories;
    } catch { /* fall through to feed render with empty categories — IDs will display */ }
    refreshFeed();
  })();

  async function refreshFeed() {
    const hasAny = await renderFeed(feedHost, categories);
    if (!hasAny) renderEmptyState(feedHost);
  }

  const homeLink = root.querySelector<HTMLAnchorElement>('#home-link');
  if (homeLink) {
    homeLink.onclick = (e) => {
      e.preventDefault();
      void navigate('/');
    };
  }

  const gearLink = root.querySelector<HTMLAnchorElement>('#gear-link');
  if (gearLink) {
    gearLink.onclick = (e) => {
      e.preventDefault();
      void navigate('/settings');
    };
  }

  const dashLink = root.querySelector<HTMLAnchorElement>('#dash-link');
  if (dashLink) {
    dashLink.onclick = (e) => {
      e.preventDefault();
      void navigate('/dashboard');
    };
  }

  const smsLink = root.querySelector<HTMLAnchorElement>('#sms-link');
  if (smsLink) {
    smsLink.onclick = (e) => {
      e.preventDefault();
      void navigate('/sms-inbox');
    };
  }

  const addIncomeBtn = root.querySelector<HTMLButtonElement>('#add-income');
  const incomeMount = root.querySelector<HTMLElement>('#income-sheet-mount');
  if (addIncomeBtn && incomeMount) {
    addIncomeBtn.addEventListener('click', async () => {
      const [accts, cats] = await Promise.all([listAccounts(), listCategories()]);
      openIncomeSheet(incomeMount, {
        accounts: accts.accounts,
        categories: cats.categories,
        onClose: () => { incomeMount.innerHTML = ''; },
        onSuccess: () => { incomeMount.innerHTML = ''; void refreshFeed(); },
      });
    });
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
